import { useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import type { CommunicationMatrixItem } from '../features/communication-matrix/types';
import { RequiredFieldsLegend } from './RequiredFieldsLegend';

const schema = z.object({
  whoCommunicates: z.string().min(1, 'Requerido').max(120),
  whatCommunicates: z.string().min(1, 'Requerido'),
  toWhomCommunicates: z.string().min(1, 'Requerido').max(120),
  whenCommunicates: z.string().min(1, 'Requerido').max(120),
  howCommunicates: z.string().min(1, 'Requerido').max(120)
});

type FormValues = z.infer<typeof schema>;

interface Props {
  roleOptions: string[];
  initialValues?: CommunicationMatrixItem | null;
  onSubmit: (values: FormValues) => void;
  onCancel?: () => void;
  loading?: boolean;
  userRole?: string;
  isSuperadmin?: boolean;
}

export function CommunicationMatrixForm({
  roleOptions,
  initialValues,
  onSubmit,
  onCancel,
  loading,
  userRole = '',
  isSuperadmin = false,
}: Props) {
  const isEditing = !!initialValues;
  const isReadonly = !isSuperadmin;
  
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: initialValues ?? {
      whoCommunicates: isSuperadmin ? '' : userRole,
      whatCommunicates: '',
      toWhomCommunicates: '',
      whenCommunicates: '',
      howCommunicates: ''
    }
  });

  useEffect(() => {
    if (initialValues) {
      reset(initialValues);
    } else {
      reset({
        whoCommunicates: isSuperadmin ? '' : userRole,
        whatCommunicates: '',
        toWhomCommunicates: '',
        whenCommunicates: '',
        howCommunicates: ''
      });
    }
  }, [initialValues, reset, isSuperadmin, userRole]);

  const effectiveWho = useCallback(() => {
    return initialValues?.whoCommunicates || userRole;
  }, [initialValues?.whoCommunicates, userRole]);

  useEffect(() => {
    if (isReadonly) {
      setValue('whoCommunicates', effectiveWho());
    }
  }, [isReadonly, effectiveWho, setValue]);

  return (
    <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Quién Comunica *</label>
        {isReadonly ? (
          <>
            <input
              type="text"
              className="w-full rounded-md border border-input bg-muted px-3 py-2 text-sm text-foreground cursor-not-allowed"
              value={effectiveWho()}
              readOnly
            />
            <input type="hidden" {...register('whoCommunicates')} />
          </>
        ) : (
          <select
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground"
            {...register('whoCommunicates')}
          >
            <option value="">Seleccionar rol…</option>
            {roleOptions.map((opt) => (
              <option key={opt} value={opt} className="bg-background text-foreground">
                {opt}
              </option>
            ))}
          </select>
        )}
        {errors.whoCommunicates && <p className="text-sm text-destructive">{errors.whoCommunicates.message}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Qué Comunica *</label>
        <input
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground placeholder:text-muted-foreground"
          type="text"
          {...register('whatCommunicates')}
          placeholder="Ej: Informe de no conformidad"
        />
        {errors.whatCommunicates && <p className="text-sm text-destructive">{errors.whatCommunicates.message}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-1">A Quién Comunica *</label>
        <select
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground"
          {...register('toWhomCommunicates')}
        >
          <option value="">Seleccionar rol…</option>
          {roleOptions.map((opt) => (
            <option key={opt} value={opt} className="bg-background text-foreground">
              {opt}
            </option>
          ))}
        </select>
        {errors.toWhomCommunicates && <p className="text-sm text-destructive">{errors.toWhomCommunicates.message}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Cuándo Comunica *</label>
        <input
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground placeholder:text-muted-foreground"
          type="text"
          {...register('whenCommunicates')}
          placeholder="Ej: Al detectar la desviación"
        />
        {errors.whenCommunicates && <p className="text-sm text-destructive">{errors.whenCommunicates.message}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Cómo Comunica *</label>
        <input
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground placeholder:text-muted-foreground"
          type="text"
          {...register('howCommunicates')}
          placeholder="Ej: Correo electrónico, reunión"
        />
        {errors.howCommunicates && <p className="text-sm text-destructive">{errors.howCommunicates.message}</p>}
      </div>

      <RequiredFieldsLegend />
      <div className="flex justify-end gap-2 pt-2">
        {onCancel && (
          <button
            type="button"
            className="rounded-md px-4 py-2 text-sm bg-secondary text-secondary-foreground hover:bg-secondary/80"
            onClick={onCancel}
          >
            Cancelar
          </button>
        )}
        <button
          type="submit"
          className="rounded-md px-4 py-2 text-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          disabled={loading}
        >
          {loading ? 'Guardando…' : isEditing ? 'Guardar' : 'Crear'}
        </button>
      </div>
    </form>
  );
}

